import { useEffect, useState } from "react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge, Input, Table, Td, Th } from "../components/ui/misc";
import { api, type PlanRow } from "../lib/api";
import { formatMoney } from "../lib/utils";

export function PlansList() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [actor, setActor] = useState<{ email: string; role: string; departmentId: string } | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  /** 正在复制的方案 id：防止连点复制出好几份，也让按钮能显示"复制中…" */
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const reload = async () => {
    try {
      const [me, list] = await Promise.all([api.me(), api.plans()]);
      setActor(me.actor);
      setPlans(list.plans);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "加载失败");
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const createPlan = async () => {
    if (!actor || name.trim().length === 0) return;
    try {
      const created = await api.createPlan({ name: name.trim(), department_id: actor.departmentId });
      setName("");
      window.location.hash = `#/plans/${created.plan.id}`;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "新建失败");
    }
  };

  /**
   * 一键复制：整份方案深拷贝成「xx（副本）」，复制完留在列表页并把新方案刷出来
   * （新方案 updated_at 最新，排在列表最前，点名字就能进去改）。
   */
  const duplicatePlan = async (plan: PlanRow) => {
    setDuplicatingId(plan.id);
    try {
      const result = await api.duplicatePlan(plan.id);
      await reload();
      setStatus(
        `已复制「${plan.name}」→「${result.plan.name}」：${result.copied.items} 个柜体、` +
          `${result.copied.drawings} 张图纸、${result.copied.components} 行组件`,
      );
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "复制失败");
      setStatus(null);
    } finally {
      setDuplicatingId(null);
    }
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>方案</CardTitle>
          <span className="text-xs text-[var(--color-muted-foreground)]">
            {actor ? `${actor.email} · ${actor.role}` : "加载中…"}
          </span>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <div className="flex gap-2">
            <Input
              placeholder="新建方案名称，例如 洛杉矶厨房项目"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void createPlan();
              }}
            />
            <Button onClick={createPlan} disabled={!actor || name.trim().length === 0}>
              新建方案
            </Button>
          </div>
          {error ? <p className="text-sm text-[var(--color-destructive)]">{error}</p> : null}
          {status ? (
            <p role="status" className="text-sm text-emerald-700">
              {status}
            </p>
          ) : null}
          <Table>
            <thead>
              <tr>
                <Th>名称</Th>
                <Th>状态</Th>
                <Th>条目</Th>
                <Th className="text-right">成本</Th>
                <Th className="text-right">报价</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <Td>
                    <a className="font-medium hover:underline" href={`#/plans/${plan.id}`}>
                      {plan.name}
                    </a>
                    {plan.parent_id ? <Badge className="ml-2">子方案</Badge> : null}
                  </Td>
                  <Td>
                    <Badge tone={plan.status === "draft" ? "muted" : "ok"}>{plan.status}</Badge>
                  </Td>
                  <Td>{plan.item_count ?? 0}</Td>
                  <Td className="text-right tabular-nums">{formatMoney(plan.total_cost_cents, "CNY")}</Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney(plan.total_price_cents, plan.currency)}
                  </Td>
                  <Td className="whitespace-nowrap text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mr-3"
                      disabled={duplicatingId !== null}
                      onClick={() => void duplicatePlan(plan)}
                      title="整份复制成一份新方案：图纸、标尺、条目与组件快照都带过去，副本从草稿起步"
                    >
                      {duplicatingId === plan.id ? "复制中…" : "复制"}
                    </Button>
                    <a
                      className="text-xs text-[var(--color-muted-foreground)] hover:underline"
                      href={`#/plans/${plan.id}`}
                    >
                      打开
                    </a>
                  </Td>
                </tr>
              ))}
              {plans.length === 0 ? (
                <tr>
                  <Td colSpan={6} className="text-center text-sm text-[var(--color-muted-foreground)]">
                    还没有方案，先新建一个。
                  </Td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
